import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from '@/components/MetricCard';
import { PieChart, ChartContainer } from '@/components/charts';
import { useEmailDeliveryStats, useEmailByDomain, useEmailBounceBreakdown } from '../hooks';
import type { PeriodFilter } from '../api';
import {
  RefreshCw, Mail, Send, CheckCircle, XCircle, Eye, MousePointer,
  ArrowLeft, TrendingUp
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Period = PeriodFilter;

export default function EmailAnalyticsPage() {
  const { t } = useTranslation('intelligence');
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');

  const { data: stats, isLoading: statsLoading, refetch, isFetching } = useEmailDeliveryStats(period);
  const { data: domains, isLoading: domainsLoading } = useEmailByDomain(period);
  const { data: bounces, isLoading: bouncesLoading } = useEmailBounceBreakdown(period);

  const periods: { value: Period; label: string }[] = [
    { value: 'day', label: t('communications.email.periods.day', 'Today') },
    { value: 'week', label: t('communications.email.periods.week', 'This Week') },
    { value: 'month', label: t('communications.email.periods.month', 'This Month') },
    { value: 'quarter', label: t('communications.email.periods.quarter', 'This Quarter') },
  ];

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '--';
    return num.toLocaleString();
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return '--';
    return `${(Math.round(value * 10) / 10)}%`;
  };

  const goBack = () => {
    navigate('/intelligence/communications');
  };

  // Bounce breakdown chart data
  const bounceChartData = [
    { name: t('communications.email.bounceTypes.hard', 'Hard Bounce'), value: bounces?.hard_bounces || 0, color: '#e74c3c' },
    { name: t('communications.email.bounceTypes.soft', 'Soft Bounce'), value: bounces?.soft_bounces || 0, color: '#f39c12' },
    { name: t('communications.email.bounceTypes.blocked', 'Blocked'), value: bounces?.blocked_bounces || 0, color: '#95a5a6' },
  ].filter(d => d.value > 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('communications.email.backToOverview', 'Back to Overview')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="h-6 w-6 text-blue-600" />
              {t('communications.email.title', 'Email Analytics')}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border bg-white overflow-hidden">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common:refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <MetricCard
          title={t('communications.email.cards.totalSent', 'Total Sent')}
          value={stats?.total_sent}
          icon={<Send className="h-5 w-5" />}
          color="blue"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.email.cards.delivered', 'Delivered')}
          value={stats?.delivered}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.email.cards.bounced', 'Bounced')}
          value={stats?.bounced}
          icon={<XCircle className="h-5 w-5" />}
          color="red"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.email.cards.opened', 'Opened')}
          value={stats?.opened}
          icon={<Eye className="h-5 w-5" />}
          color="purple"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.email.cards.clicked', 'Clicked')}
          value={stats?.clicked}
          icon={<MousePointer className="h-5 w-5" />}
          color="orange"
          loading={statsLoading}
        />
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.email.cards.deliveryRate', 'Delivery Rate')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-green-600">
                    {formatPercent(stats?.delivery_rate)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Eye className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.email.cards.openRate', 'Open Rate')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-purple-600">
                    {formatPercent(stats?.open_rate)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <MousePointer className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.email.cards.clickRate', 'Click Rate')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-orange-600">
                    {formatPercent(stats?.click_rate)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Domains + Bounce Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Domains Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.email.sections.domains', 'Top Domains')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.email.table.domain', 'Domain')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.email.table.sent', 'Sent')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.email.table.delivered', 'Delivered')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.email.table.deliveryRate', 'Delivery Rate')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.email.table.openRate', 'Open Rate')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {domainsLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : domains && domains.length > 0 ? (
                    domains.map((domain, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {domain.domain}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(domain.sent)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(domain.delivered)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={cn(
                            'font-medium',
                            (domain.delivery_rate || 0) >= 95 ? 'text-green-600' :
                            (domain.delivery_rate || 0) >= 80 ? 'text-yellow-600' : 'text-red-600'
                          )}>
                            {formatPercent(domain.delivery_rate)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatPercent(domain.open_rate)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        {t('communications.email.noDomains', 'No domain data available')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Bounce Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.email.sections.bounces', 'Bounce Analysis')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={bouncesLoading} height={300}>
              {bounceChartData.length > 0 ? (
                <PieChart
                  data={bounceChartData}
                  height={270}
                  showLegend
                  innerRadius={60}
                />
              ) : (
                <div className="flex items-center justify-center h-[270px] text-gray-400">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-400" />
                    <p>{t('communications.email.noBounces', 'No bounces recorded')}</p>
                  </div>
                </div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
